import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCurrency } from '../contexts/CurrencyContext';

export default function PricingCarousel({ billingCycle }) {
  const { getPrice, getCurrencySymbol, getSavingsText, getMonthlyBreakdown } = useCurrency();

  const pricingTiers = [
    {
      id: 'institutional_monthly',
      name: 'MONTHLY',
      subtitle: 'Flexible Access',
      priceKey: 'monthly',
      billingPeriod: 'month',
      features: [
        '25+ Institutional Indicators',
        'All Flagship Systems (Swing/Scalp Bot, Forecast Paint, Trend Exhaustion, Volatility Breakout)',
        'Access to AI Strategy Builder (Chatbot)',
        'Trade Ideas Sent via Discord',
        'Access to Former Goldman Trader (after 3 months)',
        'Full Access to All Systems & Tools',
        'Continuous Updates',
        'Priority Support'
      ],
      link: '/checkout?plan=institutional&cycle=monthly',
      buttonText: 'GET STARTED',
      buttonStyle: 'bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-black hover:from-[#D8C585] hover:to-[#BDA45E]',
      isPopular: false,
      isSoldOut: false
    },
    {
      id: 'institutional_quarterly',
      name: 'QUARTERLY',
      subtitle: 'Best Value',
      priceKey: 'quarterly',
      billingPeriod: 'quarter',
      features: [
        '25+ Institutional Indicators',
        'All Flagship Systems (Swing/Scalp Bot, Forecast Paint, Trend Exhaustion, Volatility Breakout)',
        'Access to AI Strategy Builder (Chatbot)',
        'Trade Ideas Sent via Discord',
        'Immediate Access to Former Goldman Trader',
        'Full Access to All Systems & Tools',
        'Continuous Updates',
        'Priority Support'
      ],
      link: '/checkout?plan=institutional&cycle=quarterly',
      buttonText: 'GET STARTED',
      buttonStyle: 'bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-black hover:from-[#D8C585] hover:to-[#BDA45E]',
      isPopular: false,
      isSoldOut: false
    },
    {
      id: 'institutional_lifetime',
      name: 'LIFETIME',
      subtitle: 'Ultimate Access',
      priceKey: 'lifetime',
      billingPeriod: 'lifetime',
      features: [
        '25+ Institutional Indicators',
        'All Flagship Systems (Swing/Scalp Bot, Forecast Paint, Trend Exhaustion, Volatility Breakout)',
        'Trade Ideas Sent via Discord',
        'Immediate Access to Former Goldman Trader',
        'Access to AI Strategy Builder (Chatbot)',
        'Access to Beta Indicators',
        'Offline Machine Learning Systems',
        'Full Access to All Systems & Tools',
        'Continuous Updates',
        'Priority Support',
        'Lifetime Access & Features'
      ],
      link: '/checkout?plan=institutional&cycle=lifetime',
      buttonText: 'GET LIFETIME ACCESS',
      buttonStyle: 'bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-black hover:from-[#D8C585] hover:to-[#BDA45E]',
      isPopular: false,
      isSoldOut: false,
      isLifetime: true
    },
    {
      id: 'custom',
      name: 'CUSTOM INDICATOR',
      subtitle: 'We Build Your Vision',
      monthlyPrice: null,
      yearlyPrice: null,
      yearlyTotal: null,
      features: [
        'Everything in Institutional Suite, plus:',
        'Former Goldman Trader Personally Builds Your Custom Indicator',
        'Tailored to Your Exact Specifications',
        'One-on-One Consultation Sessions',
        'Ongoing Support & Refinements',
        'Exclusive Access to Bespoke Tools'
      ],
      link: '#contact',
      buttonText: 'CONTACT US',
      buttonStyle: 'bg-neutral-900 ring-1 ring-neutral-700 text-white hover:ring-[#CBB26A] hover:text-[#CBB26A]',
      isPopular: false,
      isSoldOut: false
    }
  ];

  return (
    <div className="mb-16 pt-6">
      {/* Grid Layout for Pricing Tiers */}
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Monthly */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <PricingCard tier={pricingTiers[0]} />
          </motion.div>

          {/* Quarterly - Featured */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <PricingCard tier={pricingTiers[1]} />
          </motion.div>

          {/* Lifetime */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="md:col-span-2 lg:col-span-1"
          >
            <PricingCard tier={pricingTiers[2]} />
          </motion.div>
        </div>

        {/* Custom Indicator - Full Width Below */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="max-w-4xl mx-auto"
        >
          <PricingCard tier={pricingTiers[3]} />
        </motion.div>
      </div>
    </div>
  );
}

function PricingCard({ tier }) {
  const { getPrice, getCurrencySymbol, getSavingsText, getMonthlyBreakdown } = useCurrency();
  const isCustomTier = tier.id === 'custom';

  const price = tier.priceKey ? getPrice(tier.priceKey) : null;
  const currencySymbol = getCurrencySymbol();
  const savingsText = tier.priceKey ? getSavingsText(tier.priceKey) : tier.savings;
  const monthlyBreakdown = tier.priceKey ? getMonthlyBreakdown(tier.priceKey) : null;

  return (
    <motion.div
      whileHover={{ scale: tier.isPopular ? 1 : 1.02 }}
      className={`bg-gradient-to-br from-neutral-950/90 via-neutral-950/70 to-neutral-900/80 backdrop-blur-sm p-8 md:p-10 rounded-xl shadow-2xl shadow-black/35 transition relative h-full flex flex-col ${
        tier.isPopular
          ? 'ring-2 ring-[#CBB26A]/70'
          : 'ring-1 ring-neutral-800/70 hover:ring-[#CBB26A]/50'
      }`}
    >
      {tier.isSoldOut && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-red-600 text-white px-6 py-2 rounded-full font-black text-sm z-10">
          SOLD OUT
        </div>
      )}
      {tier.isPopular && !tier.isSoldOut && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-black px-4 py-1.5 rounded-full font-bold text-xs tracking-wide z-10">
          POPULAR
        </div>
      )}
      {savingsText && !tier.isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-600 text-white px-4 py-1.5 rounded-full font-bold text-xs tracking-wide z-10">
          {savingsText}
        </div>
      )}

      <div className="mt-6">
        <div className="text-2xl md:text-3xl font-black mb-3">{tier.name}</div>
        <div className="text-xs font-bold tracking-widest uppercase text-[#CBB26A] mb-6">{tier.subtitle}</div>

        {isCustomTier ? (
          <div className="flex items-baseline gap-2 mb-8">
            <span className="text-4xl md:text-5xl font-black">Custom</span>
            <span className="text-neutral-500 text-sm">Pricing</span>
          </div>
        ) : (
          <>
            <div className={`flex items-baseline gap-2 mb-2 ${tier.isSoldOut ? 'opacity-50' : ''}`}>
              <span className={`text-4xl md:text-5xl font-black ${tier.isSoldOut ? 'line-through' : ''}`}>
                {currencySymbol}{price}
              </span>
              {tier.billingPeriod !== 'lifetime' && (
                <span className="text-neutral-500 text-sm">/ {tier.billingPeriod}</span>
              )}
            </div>
            {tier.billingPeriod === 'lifetime' && (
              <div className="text-sm text-[#CBB26A] mb-6">
                One-time payment • Forever access
              </div>
            )}
            {monthlyBreakdown && (
              <div className="text-sm text-neutral-400 mb-6">
                {monthlyBreakdown} • Billed quarterly
              </div>
            )}
            {tier.billingPeriod === 'month' && (
              <div className="mb-6 h-5"></div>
            )}
          </>
        )}
      </div>

      <div className="space-y-4 mb-8 md:mb-10 text-sm flex-grow">
        {tier.features.map((feature, idx) => (
          <div key={idx} className="flex items-start gap-2">
            <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${feature.includes('plus:') ? 'text-[#CBB26A]' : 'text-[#CBB26A]'}`} />
            <span className={feature.includes('plus:') ? 'text-[#CBB26A] font-bold' : 'text-neutral-400'}>{feature}</span>
          </div>
        ))}
      </div>

      {tier.isSoldOut ? (
        <button className="w-full bg-[#CBB26A]/20 ring-1 ring-[#CBB26A] text-[#CBB26A] py-5 rounded-lg font-bold hover:bg-[#CBB26A]/30 transition mt-auto text-base">
          {tier.buttonText}
        </button>
      ) : (
        <Link to={tier.link} className={`w-full ${tier.buttonStyle} py-5 rounded-lg font-bold transition mt-auto block text-center text-base`}>
          {tier.buttonText}
        </Link>
      )}
    </motion.div>
  );
}
